import { useState, useEffect, useCallback } from 'react';
import { userHighlightsAPI } from '@/api/userHighlights';
import { toast } from 'sonner';

/**
 * Custom hook for managing user highlights.
 * Loads highlights from backend API and provides methods to add/remove highlights.
 * 
 * @param {string|number} chapterId - Chapter ID (can be string like 'chapter_7' or number)
 * @returns {Object} { highlights, addHighlight, removeHighlight, isTextHighlighted, loading }
 */
export function useHighlights(chapterId) {
  // Extract numeric chapter ID if format is 'chapter_7'
  const numericChapterId = typeof chapterId === 'string' && chapterId.startsWith('chapter_')
    ? parseInt(chapterId.replace('chapter_', ''), 10)
    : (typeof chapterId === 'number' ? chapterId : null);

  console.log('🔍 useHighlights called with:', { chapterId, numericChapterId, type: typeof chapterId });

  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load highlights from backend
  const loadHighlights = useCallback(async () => {
    if (!numericChapterId || isNaN(numericChapterId)) {
      console.warn('⚠️ Invalid chapter ID for highlights:', chapterId, '-> numeric:', numericChapterId);
      setHighlights([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log('🔍 Loading highlights for chapter:', numericChapterId, '(original:', chapterId, ')');
      console.log('🔍 Making API call to:', `/api/users/highlights/?chapter_id=${numericChapterId}`);
      const data = await userHighlightsAPI.getChapterHighlights(numericChapterId);
      console.log('✅ Loaded highlights:', data?.length || 0, 'highlights');
      console.log('✅ Highlights data type:', typeof data, 'Is array:', Array.isArray(data));

      // Safety check: ensure data is an array
      if (!data || !Array.isArray(data)) {
        console.warn('⚠️ Highlights data is not an array:', data);
        setHighlights([]);
        return;
      }

      if (data.length > 0) {
        console.log('✅ First highlight sample:', data[0]);
      }
      setHighlights(data);
    } catch (error) {
      console.error('❌ Failed to load highlights:', error);
      console.error('   Status:', error.response?.status);
      console.error('   Data:', error.response?.data);
      setError(error);
      // Don't show toast for load errors (might be 404 if no highlights exist)
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view highlights');
      } else if (error.response?.status !== 404) {
        toast.error('Failed to load highlights');
      }
      setHighlights([]);
    } finally {
      setLoading(false);
    }
  }, [numericChapterId]);

  // Load highlights when chapter changes
  useEffect(() => {
    loadHighlights();
  }, [loadHighlights]);

  /**
   * Add a new highlight
   * @param {string} text - The text to highlight
   * @param {Object} [options] - Additional options
   * @param {number} [options.page_number] - PDF page number
   * @param {string} [options.section_name] - PDF section name or context
   * @param {string} [options.color] - Highlight color (default: 'yellow')
   * @returns {Promise<Object|null>} Created highlight object or null on error
   */
  const addHighlight = useCallback(async (text, options = {}) => {
    if (!numericChapterId || isNaN(numericChapterId)) {
      console.error('Invalid chapter ID for highlight:', chapterId);
      return null;
    }

    if (!text || !text.trim()) {
      console.warn('Cannot add highlight: text is empty');
      return null;
    }

    try {
      const highlightData = {
        chapter_id: numericChapterId,
        text: text.trim(),
        page_number: options.page_number || null,
        section_name: options.section_name || null,
        color: options.color || 'yellow'
      };

      // Auto-Merge: Check for existing highlights that overlap with this new one
      // If new text contains old text (or vice versa), remove the old ones to prevent stacking
      if (highlights && highlights.length > 0) {
        const overlappingHighlights = highlights.filter(h => {
          // Normalize texts for comparison
          const existing = h.text.trim();
          const newTxt = text.trim();

          // Check if one contains the other
          // We check both ways: 
          // 1. Extending a highlight (new contains old) -> Remove old
          // 2. Shrinking/subset (old contains new) -> Remove old (user is likely refining selection)
          // Also check scope if applicable
          if (options.section_name && h.section_name) {
            // If scopes differ, do not merge
            if (h.section_name !== options.section_name) return false;
          }

          return newTxt.includes(existing) || existing.includes(newTxt);
        });

        if (overlappingHighlights.length > 0) {
          console.log('🧹 Auto-removing overlapping highlights:', overlappingHighlights.map(h => h.id));
          // Delete them sequentially (could be parallelized but safer sequentially for API)
          for (const h of overlappingHighlights) {
            try {
              await userHighlightsAPI.delete(h.id);
            } catch (ignored) { /* Ignore 404 */ }
          }
          // Update state immediately to reflect removal
          setHighlights(prev => prev.filter(h => !overlappingHighlights.some(oh => oh.id === h.id)));
        }
      }

      const newHighlight = await userHighlightsAPI.create(highlightData);

      // Optimistically update local state
      setHighlights(prev => [newHighlight, ...prev]);

      return newHighlight;
    } catch (error) {
      console.error('Failed to add highlight:', error);
      const errorMessage = error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        'Failed to add highlight';

      // Check if it's a duplicate (409 Conflict)
      if (error.response?.status === 409) {
        // Reload highlights to get the existing one
        await loadHighlights();
        toast.info('This text is already highlighted');
      } else {
        toast.error(errorMessage);
      }
      return null;
    }
  }, [numericChapterId, chapterId, loadHighlights]);

  /**
   * Remove a highlight
   * @param {string|number} highlightId - Highlight ID
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const removeHighlight = useCallback(async (highlightId) => {
    if (!highlightId && highlightId !== 0) {
      console.warn('Cannot remove highlight: ID is missing');
      return false;
    }

    try {
      // Convert to number - handle both string and number IDs
      let numericId;

      if (typeof highlightId === 'number') {
        numericId = highlightId;
      } else if (typeof highlightId === 'string') {
        // Try to find the highlight in the array first (most reliable)
        const highlight = highlights.find(h =>
          h.id.toString() === highlightId ||
          h.id === parseInt(highlightId, 10)
        );

        if (highlight) {
          numericId = highlight.id;
        } else {
          // Try parsing as number directly
          numericId = parseInt(highlightId, 10);
          if (isNaN(numericId)) {
            console.error('Invalid highlight ID - cannot parse:', highlightId);
            toast.error('Invalid highlight ID');
            return false;
          }
        }
      } else {
        console.error('Invalid highlight ID type:', typeof highlightId, highlightId);
        return false;
      }

      console.log('🗑️ Removing highlight from backend:', numericId, '(original:', highlightId, ')');

      // Delete from backend
      await userHighlightsAPI.delete(numericId);

      // Optimistically update local state - remove the highlight
      setHighlights(prev => {
        const filtered = prev.filter(h => h.id !== numericId);
        console.log('✅ Updated highlights state. Removed:', numericId, 'Remaining:', filtered.length);
        return filtered;
      });

      return true;
    } catch (error) {
      console.error('Failed to remove highlight:', error);
      const errorMessage = error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        'Failed to remove highlight';
      toast.error(errorMessage);
      return false;
    }
  }, [highlights]);

  /**
   * Check if a text is already highlighted
   * @param {string} text - Text to check
   * @returns {boolean} True if text is highlighted
   */
  const isTextHighlighted = useCallback((text) => {
    if (!text) return false;
    const normalizedText = text.trim();
    return highlights.some(h => h.text.trim() === normalizedText);
  }, [highlights]);

  return {
    highlights,
    addHighlight,
    removeHighlight,
    isTextHighlighted,
    loading,
    error,
    reload: loadHighlights
  };
}
